import React, { useState } from "react";

import { replaceEmployees, deleteEmployee, fetchEmployees } from "EmployerApplication/Actions";
import { useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import MinimumEmployeesMessage from "EmployerApplication/Components/MinimumEmployeesMessage";
import OwnersAsEmployeesForm from "EmployerApplication/Components/OwnersAsEmployeesForm";
import PartnerEmployeeTable from "EmployerApplication/Components/PartnerEmployeeTable";
import { getHasMinimumEmployees, mergeEmployeesWithEligibityForm } from "EmployerApplication/MinimumEmployeesUtils";
import { RESOURCE_STATES } from "EmployerApplication/Reducers";
import { getCompanyApplication } from "EmployerApplication/Selectors";
import { reviewEmployeeEmailsURL, selectContributionTiersURL, companyInformationURL, api } from "EmployerApplication/urls";
import { useGetSurveyQuery } from "LargeGroupIntent/api";
import { employeeAcaStatus } from "LargeGroupIntent/utils";
import { values, mapValues } from "lodash";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { reduxForm, formValueSelector, Field, change } from "redux-form";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import commaListify from "Utils/commaListify";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";

import { TitleSmall } from "Cobalt/Header";
import { Modal, ModalBody, ModalHeader, ModalFooter, useModal } from "Cobalt/Modal";
import { Table, HeaderTR, TH, TR, TD } from "Cobalt/TableV2";
import { SIFormRadioGroup } from "Common/forms";
import { Row, Col } from "Common/Grid";
import NavigationControls from "Common/NavigationControls";
import PartnerContext, { usePartnerContext } from "Common/PartnerContext";
import { windowSizeAtLeast } from "Common/Responsive";
import { Body2, SubBody } from "Theme/BodyText";
import Button from "Theme/Button";
import { useThemeContext } from "Theme/Context";
import { SurfaceSection, SurfaceHeader } from "Theme/Surface";

const HeaderRow = ({ employeeType, largeGroup }) => {
  return (
    <HeaderTR>
      {windowSizeAtLeast("md") ? (
        <>
          <TH>{employeeType}</TH>
          <TH>{largeGroup?.acaReporting && "ACA Status"}</TH>
          <TH>Offer Benefits?</TH>
        </>
      ) : (
        <>
          <TH colSpan="2">{employeeType}</TH>
          <TH>Offer Benefits?</TH>
        </>
      )}
    </HeaderTR>
  );
};
const EmployeeRow = ({ employee, handleExceptionStart, largeGroup }) => {
  return (
    <TR highlightFirstCell={false} py={3}>
      {windowSizeAtLeast("md") ? (
        <>
          <TD>{employee.last_name_first}</TD>
          <TD>{largeGroup?.acaReporting && employeeAcaStatus(employee, largeGroup)}</TD>
        </>
      ) : (
        <TD colSpan={2}>
          {`${employee.last_name_first}`}
          {largeGroup?.acaReporting && (
            <>
              <br />
              {employeeAcaStatus(employee, largeGroup)}
            </>
          )}
        </TD>
      )}
      <TD>
        <Field
          component={SIFormRadioGroup}
          name={`employees["${employee.hash_id}"].is_eligible`}
          inline
          options={[
            ["true", "Yes"],
            ["false", "No"],
          ]}
          onChange={(value) => {
            handleExceptionStart(employee, value);
          }}
        />
      </TD>
    </TR>
  );
};

const NoEmployeesRow = () => (
  <TR>
    <TD colSpan={windowSizeAtLeast("md") ? 3 : 2}>No employees in payroll.</TD>
  </TR>
);

const LoadingEmployeesRow = ({ partnerCasualName }) => {
  return (
    <TR>
      <TD className="text-center" colSpan={windowSizeAtLeast("md") ? 3 : 2}>
        {`Loading employees from ${partnerCasualName}. Please wait...`}
      </TD>
    </TR>
  );
};

export const PartnerAddEmployeesDumb = ({
  employees,
  handleSubmit,
  submitting,
  deleteEmployee,
  isEmployeesSectionLoading,
  fetchEmployees,
  eligibleEmployees,
  ownersAsEmployees,
}) => {
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();

  const { intuit, cobalt } = useThemeContext();
  const showCobalt = cobalt && !intuit;
  const { partnerCasualName } = usePartnerContext();

  const { data: largeGroup, isLoading: isLoadingLargeGroup, isError, error } = useGetSurveyQuery();
  const isLoading = isLoadingLargeGroup || isEmployeesSectionLoading;

  const allowSingleOwner = companyApplication?.allowSingleOwner;
  const hasMinimumEmployees = getHasMinimumEmployees(eligibleEmployees, allowSingleOwner);

  const { isShowing: isShowingExceptionModal, show: showExceptionModal, hide: hideExceptionModal } = useModal();

  const [addingExceptionFor, setAddingExceptionFor] = useState(null);

  const handleExceptionStart = (employee, isEligible) => {
    if (isEligible === "false") {
      setAddingExceptionFor(employee);
      showExceptionModal();
    }
  };

  const handleExceptionCancel = () => {
    hideExceptionModal();
    if (addingExceptionFor) {
      change(`employees[${addingExceptionFor.hash_id}].is_eligible`, "true");
    }
  };

  const handleExceptionConfirm = () => {
    hideExceptionModal();
    const employee = addingExceptionFor;
    if (employee) {
      change(`employees[${employee.hash_id}].is_eligible`, "false");
      AnalyticsTracking.trackEvent("add_employee_decline_benefit", {
        employee_not_offered_benefit: employee.hash_id,
      });
    }
  };

  const renderSection = (employeeType, employees) => {
    return (
      <Table>
        <thead>
          <HeaderRow employeeType={employeeType} largeGroup={largeGroup} />
        </thead>
        <tbody>
          {isLoading ? (
            <LoadingEmployeesRow partnerCasualName={partnerCasualName} />
          ) : employees.length == 0 ? (
            <NoEmployeesRow />
          ) : (
            employees.map((employee) => (
              <EmployeeRow key={employee.hash_id} employee={employee} handleExceptionStart={handleExceptionStart} largeGroup={largeGroup} />
            ))
          )}
        </tbody>
      </Table>
    );
  };

  const w2Employees = employees.filter((e) => e.employee_type === "full_time");
  const contractors = employees.filter((e) => e.employee_type === "1099");
  const owners = employees.filter((e) => e.employee_type === "owner");

  const contractorsAndOwners = employees.filter((e) => e.employee_type === "1099" || e.employee_type === "owner");

  const managedTypes = (managedW2s, managed1099s, managedOwners) =>
    [managedW2s && "employees", managed1099s && "contractors", managedOwners && "owners"].filter((e) => e);

  const partnerEmployees = (siManaged1099s, siManagedOwners) => {
    if (siManaged1099s && siManagedOwners) {
      return contractorsAndOwners;
    }
    if (siManaged1099s && !siManagedOwners) {
      return contractors;
    }
    if (!siManaged1099s && siManagedOwners) {
      return owners;
    }
    return [];
  };

  const siManagedList = (props) =>
    commaListify(
      managedTypes(props.siManagedW2s, props.siManaged1099s, props.siManagedOwners).map((n) => n.charAt(0).toUpperCase() + n.slice(1)),
      "&",
    );

  return (
    <div>
      <SurfaceSection title="Eligible Employees">
        <Row pb={3}>
          <Col role="cell">
            <Body2>
              <PartnerContext.Consumer>
                {(props) =>
                  `These are all of the ${commaListify(
                    managedTypes(props.partnerManagedW2s, props.partnerManaged1099s, props.partnerManagedOwners),
                  )} that were imported from ${props.partnerName}. You can select whether or not to offer each of them insurance.`
                }
              </PartnerContext.Consumer>
            </Body2>
            <MinimumEmployeesMessage allowSingleOwner={allowSingleOwner} hasMinimumEmployees={hasMinimumEmployees} />
          </Col>
        </Row>
        <Row>
          <Col>
            {renderSection("Employees on Payroll", w2Employees)}
            <PartnerContext.Consumer>
              {(props) => props.partnerManaged1099s && contractors.length > 0 && renderSection("1099 Contractors", contractors)}
            </PartnerContext.Consumer>
            <PartnerContext.Consumer>{(props) => props.partnerManagedOwners && owners.length > 0 && renderSection("Owners", owners)}</PartnerContext.Consumer>
          </Col>
        </Row>
        <Row>
          <Col>
            <PartnerContext.Consumer>
              {(props) => (
                <SubBody role="cell" color="muted" pt={3} mt={2}>
                  Don't see all your{" "}
                  {commaListify(
                    managedTypes(props.partnerManagedW2s, props.partnerManaged1099s, props.partnerManagedOwners),

                    "or",
                  )}
                  ? Add them to {props.partnerName} then click{"\u00A0"}
                  <Button
                    aria-label={`Sync with ${props.partnerName}`}
                    classicProps={{ color: "link", onDarkBackground: true }}
                    intuitProps={{ color: "link", onDarkBackground: true }}
                    cobaltProps={{ color: "tertiary" }}
                    disabled={isEmployeesSectionLoading}
                    onClick={fetchEmployees}
                    style={{ verticalAlign: "baseline" }}
                    p={0}
                    type="button"
                  >
                    here
                  </Button>
                  {"\u00A0"}to sync. New hires will be invited to enroll in benefits when they are added to {props.partnerName}.
                </SubBody>
              )}
            </PartnerContext.Consumer>
          </Col>
        </Row>
        <OwnersAsEmployeesForm employees={employees} ownersAsEmployees={ownersAsEmployees} />
      </SurfaceSection>
      <PartnerContext.Consumer>
        {(props) =>
          (props.siManaged1099s || props.siManagedOwners) && (
            <>
              {!showCobalt && <SurfaceHeader>{siManagedList(props)}</SurfaceHeader>}

              <SurfaceSection title={siManagedList(props)}>
                <Body2 pb={4} role="cell">
                  If you have{" "}
                  {commaListify(
                    managedTypes(props.siManagedW2s, props.siManaged1099s, props.siManagedOwners),

                    "or",
                  )}{" "}
                  who are not in {props.partnerName}, you may add them here.
                </Body2>
                <Row>
                  <Col>
                    <PartnerEmployeeTable
                      onDeleteEmployee={deleteEmployee}
                      employees={partnerEmployees(props.siManaged1099s, props.siManagedOwners)}
                      include1099={props.siManaged1099s}
                      includeOwner={props.siManagedOwners}
                    />
                  </Col>
                </Row>
              </SurfaceSection>
            </>
          )
        }
      </PartnerContext.Consumer>
      <form onSubmit={handleSubmit}>
        <NavigationControls
          backLink={companyInformationURL}
          onClickBack={() => {
            AnalyticsTracking.trackEvent("back_button_clicked", {
              location: "add_employee_page",
            });
          }}
          saveDisabled={submitting || !hasMinimumEmployees}
          submitting={submitting}
        />
      </form>
      <Modal show={isShowingExceptionModal} onHide={handleExceptionCancel}>
        <ModalHeader closeButton>
          <TitleSmall mt={0} mb={0}>
            Confirm Ineligible Employee
          </TitleSmall>
        </ModalHeader>
        <ModalBody>
          {addingExceptionFor?.is_large_group && addingExceptionFor?.work_status === "full_time" && (
            <p>
              {addingExceptionFor?.full_name} is listed as a full-time employee in {partnerCasualName}. Marking the employee as ineligible may violate
              Affordable Care Act rules and lead to tax penalties.
            </p>
          )}
          {addingExceptionFor?.is_large_group && addingExceptionFor?.work_status === "part_time" && addingExceptionFor?.part_time_eligible_at && (
            <p>
              {`${addingExceptionFor?.full_name} was determined to be eligible for benefits on ${addingExceptionFor?.part_time_eligible_at}. Marking the employee as ineligible may violate Affordable Care Act rules and lead to tax penalties.`}
            </p>
          )}
          <p>
            For a group medical plan, you must offer coverage to all full-time active employees. Employees who don't need coverage will be able to decline later
            in the process. You should only make an employee ineligible if they{" "}
            {addingExceptionFor?.is_large_group ? "are part-time and do not qualify under ACA or " : " are part-time or "} no longer working at your company.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="gray" mr={3} onClick={handleExceptionCancel} size="medium">
            Cancel
          </Button>
          <Button color="blue" onClick={handleExceptionConfirm} size="medium">
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

const formSelector = formValueSelector("eligibility");

const parseValues = (values) => ({
  covering_part_time: values.coveringPartTime,
  employees: mapValues(values.employees, (e) => ({
    is_eligible: JSON.parse(e.is_eligible),
  })),
});

export const submitCoverageExceptions = (values, dispatch, props) =>
  fetchWithCSRFToken(api.setCoverageExceptions, {
    method: "POST",
    body: JSON.stringify({ company_id_token: companyToken(), ...parseValues(values) }),
  })
    .then((response) => {
      if (response.ok) {
        return response.json();
      }
      throw new Error(`Unable to save, HTTP ${response.status}`);
    })
    .then((response) => {
      dispatch(replaceEmployees(response.employees));
      AnalyticsTracking.trackEvent("add_employees_save_clicked", {});
      props.history.push(props.hasTieredContributions ? selectContributionTiersURL : reviewEmployeeEmailsURL);
    });

const withReduxForm = reduxForm({
  form: "eligibility",
  destroyOnUnmount: false,
  enableReinitialize: true,
  onSubmit: (values, dispatch, props) => {
    submitCoverageExceptions(values, dispatch, props);
    if (props.ownerAsEmployeeName) {
      AnalyticsTracking.trackEvent("cta_clicked", {
        label: "continued_with_owners_identified",
      });
    }
  },
});

const mapStateToProps = (state) => {
  const employees = values(state.employees) || [];
  const employeeEligiblity = formSelector(state, "employees");
  const eligibleEmployees = mergeEmployeesWithEligibityForm(employees, employeeEligiblity);

  return {
    employees,
    eligibleEmployees,
    hasTieredContributions: getCompanyApplication(state)?.hasTieredContributions,
    coveringPartTime: state.coveringPartTime,
    initialValues: {
      employees: values(state.employees).reduce((acc, e) => {
        acc[e.hash_id] = {
          is_eligible: e.is_eligible.toString(),
        };

        return acc;
      }, {}),
    },

    isEmployeesSectionLoading: state.resourceStates.employees == RESOURCE_STATES.LOADING,
    getFormValue: (field) => formSelector(state, field),
    ownersAsEmployees: state.ownersAsEmployees,
    ownerAsEmployeeName: state.ownersAsEmployees?.ownerAsEmployeeName,
  };
};

const mapDispatchToProps = {
  change,
  deleteEmployee,
  fetchEmployees,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRouter(withRedux(withReduxForm(PartnerAddEmployeesDumb)));
